import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TDispatch } from '../../redux';
import { EMoneySource, publishOffer, setPlacingMoneySource } from '../../redux/reducers';
import { selectAnalyticsOfferStatus, selectAnalyticsProduct } from '../../selectors/analytics';
import { selectCurrentBet } from '../../selectors/auction';
import { selectBetPosition } from '../../selectors/betPosition';
import {
  selectTotalAmount,
  selectIsPublishButtonPending,
  selectPublicationButtonText,
  selectIsPublishButtonDisabled,
  selectIsAutoProlongChecked,
} from '../../selectors/checkoutInfo';
import { selectIsShortForm } from '../../selectors/layout';
import { selectOfferId } from '../../selectors/offer';
import { selectShouldPayByCard } from '../../selectors/publishAvailable';
import { selectActiveServiceId, selectIsSubagentLimitExceed } from '../../selectors/publishTerms';
import { IAdaptiveSettings } from '../../types/adaptiveSettings';
import { getAnalyticsSiteType } from '../../utils';
import { openModal } from '../../utils/modals';
import { ApplicationContext } from '../ApplicationContext';
import { PublishButton } from './PublishButton';
import { trackPayByCardModalOpen, trackPublishByMoneyFailed, trackPublishClick } from './tracking';
import { getFormId } from '../../../browser/services/formId';

export const PublishButtonContainer = () => {
  const totalPrice = useSelector(selectTotalAmount);
  const isPending = useSelector(selectIsPublishButtonPending);
  const isShortForm = useSelector(selectIsShortForm);
  const analyticsOfferStatus = useSelector(selectAnalyticsOfferStatus);
  const text = useSelector(selectPublicationButtonText);
  const shouldPayByCard = useSelector(selectShouldPayByCard);
  const offerId = useSelector(selectOfferId);
  const isDisabled = useSelector(selectIsPublishButtonDisabled);
  const currentBet = useSelector(selectCurrentBet);
  const analyticsProduct = useSelector(selectAnalyticsProduct);
  const betPosition = useSelector(selectBetPosition);
  const selectedServiceId = useSelector(selectActiveServiceId)!;
  const isAutoprolong = useSelector(selectIsAutoProlongChecked)!;
  const isSubagentLimitExceed = useSelector(selectIsSubagentLimitExceed)!;

  const { offerPosition = null, page = null } = betPosition || {};

  const { config } = React.useContext(ApplicationContext);
  const { deviceType } = config.get<IAdaptiveSettings>('adaptiveSettings') || {};

  const siteType = getAnalyticsSiteType(deviceType);

  const dispatch = useDispatch<TDispatch>();

  const handleButtonClick = React.useCallback(() => {
    if (isSubagentLimitExceed) {
      dispatch(setPlacingMoneySource(EMoneySource.Self));

      return;
    }

    if (isDisabled) {
      return;
    }

    trackPublishClick({
      analyticsOfferStatus,
      analyticsProduct,
      totalPrice: totalPrice.price,
      currentBet,
      selectedServiceId,
      offerPosition,
      page,
      isAutoprolong,
      formId: getFormId(),
    });

    if (shouldPayByCard) {
      openModal('payByCard', { options: { source: 'add_offer' } });

      trackPayByCardModalOpen(analyticsOfferStatus);
      trackPublishByMoneyFailed({ offerId, isShortForm, siteType, analyticsOfferStatus });
    } else {
      dispatch(publishOffer({ isPremoderationSkip: false }));
    }
  }, [
    dispatch,
    totalPrice,
    isShortForm,
    analyticsOfferStatus,
    siteType,
    shouldPayByCard,
    offerId,
    isDisabled,
    currentBet,
    selectedServiceId,
    offerPosition,
    page,
    isAutoprolong,
    isSubagentLimitExceed,
  ]);

  return <PublishButton text={text} handleButtonClick={handleButtonClick} isPending={isPending} />;
};
